import React, { useState } from 'react';
import { Database, Cloud, Zap, CheckCircle, XCircle, Settings, ExternalLink, AlertCircle, Plus } from 'lucide-react';

const Integrations = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const integrations = [
    {
      id: 1,
      name: 'ChromaDB',
      category: 'Vector Database',
      description: 'Open-source embedding database for building AI applications',
      logo: '🔷',
      status: 'connected',
      lastSync: '2 hours ago',
      connectionType: 'Direct API',
      features: ['Vector Storage', 'Metadata Filtering', 'Batch Operations']
    },
    {
      id: 2,
      name: 'Pinecone',
      category: 'Vector Database',
      description: 'Managed vector database for high-performance applications',
      logo: '🌲',
      status: 'connected',
      lastSync: '5 minutes ago',
      connectionType: 'Cloud API',
      features: ['Real-time Indexing', 'Namespaces', 'Hybrid Search']
    },
    {
      id: 3,
      name: 'Qdrant',
      category: 'Vector Database',
      description: 'Vector similarity search engine with extended filtering support',
      logo: '⚡',
      status: 'connected',
      lastSync: '1 hour ago',
      connectionType: 'REST API',
      features: ['Payload Filtering', 'Quantization', 'Snapshots']
    },
    {
      id: 4,
      name: 'Weaviate',
      category: 'Vector Database',
      description: 'Cloud-native vector database with GraphQL interface',
      logo: '🔺',
      status: 'disconnected',
      lastSync: 'Never',
      connectionType: 'GraphQL API',
      features: ['Schema Management', 'Multi-tenancy', 'Vectorization']
    },
    {
      id: 5,
      name: 'AWS S3',
      category: 'Cloud Storage',
      description: 'Object storage service for backup and archival',
      logo: '☁️',
      status: 'connected',
      lastSync: '30 minutes ago',
      connectionType: 'AWS SDK',
      features: ['Backup Storage', 'Archive Management', 'Versioning']
    },
    {
      id: 6,
      name: 'Google Cloud Storage',
      category: 'Cloud Storage',
      description: 'Unified object storage for developers and enterprises',
      logo: '🌐',
      status: 'connected',
      lastSync: '15 minutes ago',
      connectionType: 'Google API',
      features: ['Multi-region', 'Lifecycle Management', 'IAM']
    },
    {
      id: 7,
      name: 'Azure Blob Storage',
      category: 'Cloud Storage',
      description: 'Massively scalable object storage for unstructured data',
      logo: '🔷',
      status: 'disconnected',
      lastSync: 'Never',
      connectionType: 'Azure SDK',
      features: ['Hot/Cool/Archive Tiers', 'Encryption', 'Redundancy']
    },
    {
      id: 8,
      name: 'Slack',
      category: 'Notifications',
      description: 'Send migration alerts and updates to Slack channels',
      logo: '💬',
      status: 'connected',
      lastSync: '10 minutes ago',
      connectionType: 'Webhook',
      features: ['Real-time Alerts', 'Custom Messages', 'Channel Routing']
    },
    {
      id: 9,
      name: 'Discord',
      category: 'Notifications',
      description: 'Receive migration notifications in Discord servers',
      logo: '🎮',
      status: 'disconnected',
      lastSync: 'Never',
      connectionType: 'Webhook',
      features: ['Embeds', 'Mentions', 'Multiple Servers']
    },
    {
      id: 10,
      name: 'Datadog',
      category: 'Monitoring',
      description: 'Monitor migration performance and system metrics',
      logo: '📊',
      status: 'connected',
      lastSync: '5 minutes ago',
      connectionType: 'Agent API',
      features: ['Custom Metrics', 'Dashboards', 'Alerting']
    },
    {
      id: 11,
      name: 'Prometheus',
      category: 'Monitoring',
      description: 'Time-series monitoring for migration analytics',
      logo: '🔥',
      status: 'disconnected',
      lastSync: 'Never',
      connectionType: 'HTTP Endpoint',
      features: ['Metrics Export', 'Grafana Compatible', 'Alertmanager']
    },
    {
      id: 12,
      name: 'GitHub',
      category: 'Version Control',
      description: 'Sync migration scripts and configurations to GitHub',
      logo: '🐙',
      status: 'connected',
      lastSync: '1 day ago',
      connectionType: 'OAuth',
      features: ['Auto Commit', 'Branch Protection', 'Webhooks']
    }
  ];

  const categories = [
    { id: 'all', label: 'All Integrations', count: integrations.length },
    { id: 'Vector Database', label: 'Vector Databases', count: integrations.filter(i => i.category === 'Vector Database').length },
    { id: 'Cloud Storage', label: 'Cloud Storage', count: integrations.filter(i => i.category === 'Cloud Storage').length },
    { id: 'Notifications', label: 'Notifications', count: integrations.filter(i => i.category === 'Notifications').length },
    { id: 'Monitoring', label: 'Monitoring', count: integrations.filter(i => i.category === 'Monitoring').length },
    { id: 'Version Control', label: 'Version Control', count: integrations.filter(i => i.category === 'Version Control').length }
  ];

  const filteredIntegrations = activeFilter === 'all' 
    ? integrations 
    : integrations.filter(i => i.category === activeFilter);

  const connectedCount = integrations.filter(i => i.status === 'connected').length;
  const disconnectedCount = integrations.filter(i => i.status === 'disconnected').length;

  const getStatusColor = (status) => {
    return status === 'connected' ? 'text-emerald-400' : 'text-gray-500';
  };

  const getStatusBg = (status) => {
    return status === 'connected' ? 'bg-emerald-500/20' : 'bg-gray-500/20';
  };

  const getStatusIcon = (status) => {
    return status === 'connected' ? CheckCircle : XCircle;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Integrations</h1>
          <p className="text-gray-400 mt-2">Connect and manage your database integrations</p>
        </div>
        <button className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium">
          <Plus className="w-4 h-4" />
          Add Integration
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Total Integrations</span>
            <Database className="w-5 h-5 text-gray-500" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">{integrations.length}</div>
          <div className="text-xs text-blue-400">Across {categories.length - 1} categories</div>
        </div>

        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Connected</span>
            <CheckCircle className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">{connectedCount}</div>
          <div className="text-xs text-emerald-400">Active and syncing</div>
        </div>

        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm font-medium">Available</span>
            <Zap className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">{disconnectedCount}</div>
          <div className="text-xs text-amber-400">Ready to connect</div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveFilter(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeFilter === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              {category.label}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                activeFilter === category.id
                  ? 'bg-blue-500'
                  : 'bg-gray-700'
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredIntegrations.map((integration) => {
          const StatusIcon = getStatusIcon(integration.status);
          return (
            <div
              key={integration.id}
              className="bg-[#141414] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-all duration-300"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-800 rounded-lg flex items-center justify-center text-2xl">
                    {integration.logo}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{integration.name}</h3>
                    <p className="text-sm text-gray-500">{integration.category}</p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(
                    integration.status
                  )} ${getStatusColor(integration.status)}`}
                >
                  <StatusIcon className="w-3.5 h-3.5" />
                  {integration.status === 'connected' ? 'Connected' : 'Not Connected'}
                </span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-400 mb-4">{integration.description}</p>

              {/* Features */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Features</p>
                <div className="flex flex-wrap gap-2">
                  {integration.features.map((feature, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Connection Info */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Cloud className="w-3.5 h-3.5" />
                    {integration.connectionType}
                  </div>
                  {integration.status === 'connected' && (
                    <div className="flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-emerald-500" />
                      Synced {integration.lastSync}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {integration.status === 'connected' ? (
                    <>
                      <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors group">
                        <Settings className="w-4 h-4 text-gray-500 group-hover:text-white" />
                      </button>
                      <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors group">
                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-white" />
                      </button>
                    </>
                  ) : (
                    <button className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors">
                      Connect
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-blue-400 font-semibold mb-2">Need Help with Integrations?</h3>
            <p className="text-sm text-gray-400 mb-4">
              Our integration guides provide step-by-step instructions for connecting each service. 
              Check out our documentation or contact support for assistance.
            </p>
            <div className="flex gap-3">
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors">
                View Documentation
              </button>
              <button className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center text-sm text-gray-500 py-4">
        © 2024 VectorMigrate. All rights reserved.
      </footer>
    </div>
  );
};

export default Integrations;